import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useSession } from '../lib/useSession';

// Where email-confirmation / OAuth links should land. Deterministic prod URL
// (overridable) so the email never points at localhost or a preview host.
// NOTE: this must also be set as Supabase Auth "Site URL" + allowed Redirect
// URL in the dashboard — that dashboard value is the real root fix.
const AUTH_REDIRECT_URL =
  process.env.REACT_APP_AUTH_REDIRECT_URL || 'https://www.extractstudy.com';

// Full-page Login / Sign Up screen (mode: 'login' | 'signup').
const AuthPage = ({ mode }) => {
  const isSignup = mode === 'signup';
  const navigate = useNavigate();
  const { session } = useSession();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState(''); // '', 'submitting', 'confirm', 'error'
  const [errorMsg, setErrorMsg] = useState('');

  // Already signed in → no reason to be here.
  useEffect(() => {
    if (session) navigate('/', { replace: true });
  }, [session, navigate]);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    const mail = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
      setStatus('error');
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    if (password.length < 6) {
      setStatus('error');
      setErrorMsg('Password must be at least 6 characters.');
      return;
    }
    if (isSignup && (!firstName.trim() || !lastName.trim())) {
      setStatus('error');
      setErrorMsg('Please enter your first and last name.');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      if (isSignup) {
        const { data, error } = await supabase.auth.signUp({
          email: mail,
          password,
          options: {
            emailRedirectTo: AUTH_REDIRECT_URL,
            data: {
              first_name: firstName.trim(),
              last_name: lastName.trim(),
              full_name: `${firstName.trim()} ${lastName.trim()}`,
            },
          },
        });
        if (error) {
          setStatus('error');
          setErrorMsg(error.message);
          return;
        }
        // If email confirmation is required, there is no session yet.
        if (data.session) {
          navigate('/', { replace: true });
        } else {
          setStatus('confirm');
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: mail,
          password,
        });
        if (error) {
          setStatus('error');
          setErrorMsg(error.message);
          return;
        }
        navigate('/', { replace: true });
      }
    } catch (_) {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  const handleGoogle = async () => {
    setErrorMsg('');
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: AUTH_REDIRECT_URL },
      });
      if (error) {
        setStatus('error');
        setErrorMsg(
          'Google sign-in isn’t enabled yet. Use email and password for now.'
        );
      }
    } catch (_) {
      setStatus('error');
      setErrorMsg(
        'Google sign-in isn’t enabled yet. Use email and password for now.'
      );
    }
  };

  return (
    <div className="min-h-screen app-shell flex flex-col items-center justify-center px-4 py-10 transition-colors duration-300">
      <div className="pointer-events-none fixed inset-0 opacity-65 mix-blend-screen" style={{ background: 'radial-gradient(60% 50% at 20% 30%, rgba(139,208,255,0.10), transparent), radial-gradient(45% 40% at 80% 20%, rgba(74,163,255,0.08), transparent)' }}></div>

      <Link to="/" className="relative mb-8">
        <span className="section-title brand-title text-4xl sm:text-5xl font-bold">
          ExtractStudy
        </span>
      </Link>

      <div
        className="relative glass-panel w-full max-w-md rounded-2xl border border-[var(--border)] p-7 shadow-xl"
        style={{ boxShadow: '0 0 40px rgba(0,0,0,0.40)', animation: 'slideUp 0.3s ease-out' }}
      >
        <h1 className="text-2xl font-semibold text-[var(--text)] mb-1">
          {isSignup ? 'Create your account' : 'Welcome back'}
        </h1>
        <p className="text-[var(--muted)] text-sm mb-6">
          {isSignup
            ? 'Sign up to save words to your Vocabulary Book.'
            : 'Log in to access your Vocabulary Book.'}
        </p>

        {status === 'confirm' ? (
          <div className="text-[var(--text)] text-sm space-y-3">
            <div className="text-3xl">📬</div>
            <p className="font-medium">Confirm your email to finish signing up.</p>
            <p className="text-[var(--muted)]">
              We sent a confirmation link to{' '}
              <span className="text-[var(--text)] break-all">{email}</span>. Click it,
              then come back and log in.
            </p>
            <Link
              to="/login"
              className="btn-primary text-gray-900 inline-block py-2 px-4 rounded-lg transition duration-200 mt-2"
            >
              Go to log in
            </Link>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={handleGoogle}
              className="btn-secondary w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium border border-[var(--border)] mb-4"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-3 my-4">
              <div className="h-px flex-1 bg-[var(--border)]"></div>
              <span className="text-[var(--muted)] text-xs">or</span>
              <div className="h-px flex-1 bg-[var(--border)]"></div>
            </div>

            <form onSubmit={handleEmailAuth} className="space-y-3">
              {isSignup && (
                <div className="flex gap-3">
                  <input
                    type="text"
                    className="w-full p-3 rounded-xl input-surface focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)] focus:outline-none"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="given-name"
                    autoFocus
                  />
                  <input
                    type="text"
                    className="w-full p-3 rounded-xl input-surface focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)] focus:outline-none"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    autoComplete="family-name"
                  />
                </div>
              )}
              <input
                type="email"
                className="w-full p-3 rounded-xl input-surface focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)] focus:outline-none"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                {...(!isSignup ? { autoFocus: true } : {})}
              />
              <input
                type="password"
                className="w-full p-3 rounded-xl input-surface focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)] focus:outline-none"
                placeholder={isSignup ? 'Create a password (6+ chars)' : 'Password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={isSignup ? 'new-password' : 'current-password'}
              />
              {status === 'error' && (
                <p className="text-red-400 text-xs">{errorMsg}</p>
              )}
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="btn-primary text-gray-900 w-full py-2.5 px-4 rounded-lg transition duration-200 font-medium disabled:opacity-60"
              >
                {status === 'submitting'
                  ? 'Please wait…'
                  : isSignup
                  ? 'Sign up'
                  : 'Log in'}
              </button>
            </form>

            <p className="text-[var(--muted)] text-sm mt-5 text-center">
              {isSignup ? (
                <>
                  Already have an account?{' '}
                  <Link to="/login" className="text-[var(--accent)] hover:underline">
                    Log in
                  </Link>
                </>
              ) : (
                <>
                  New here?{' '}
                  <Link to="/signup" className="text-[var(--accent)] hover:underline">
                    Create an account
                  </Link>
                </>
              )}
            </p>
          </>
        )}
      </div>

      <Link to="/" className="relative mt-6 text-[var(--muted)] text-sm hover:text-[var(--text)] transition-colors">
        ← Back to ExtractStudy
      </Link>
    </div>
  );
};

export default AuthPage;
